import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { Drawer, Button as BpButton } from "@blueprintjs/core"
import css from "../ChatHistory.module.css"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import {
  hideMobileMap,
  isMobileMapVisible,
  showMobileMap,
} from "@/reducers/overlaysSlice"
import { selectLocationJidFromChatJid } from "@/reducers/selectors"
import { isMobile } from "@/utils/detectDevice"
import RouteView from "./RouteView"
import { mobileRoutes, setMobileRoute } from "@/routes/routes"
import useContainerResize from "../../hooks/useContainerResize"
import { Chat } from "@/reducers/chatsSlice"
import useIsReadOnlyMap from "@/hooks/useIsReadOnlyMap"
import { TimeProgressBar } from "@/components/tacho/TimeProgressBar"
import { useChatTachos } from "@/hooks/useChatTachos"
import { useMediaQuery } from "@react-hookz/web"
import { cn } from "@/lib/utils"
import VehiclePlannedOrders from "@/components/timeline/planner/VehiclePlannedOrders"
import { findPlannedOrdersForVehicle } from "@/reducers/plannedOrdersSlice"
import { fetchPlannedOrdersIfStale } from "@/reducers/plannedOrdersThunks"
import { useNaviConfig } from "@/hooks/useNaviConfig"
import { XmppContext } from "@/stream/xmppClient"
import { extractVhostFromUser } from "@/jidUtils"
import { useTranslation } from "react-i18next"

type ChatMapProps = {
  chat: Chat
  driverJid: string
  chatContainerRef: React.RefObject<HTMLDivElement>
  onHide?: () => void
  /** Drop the tabs, the hide button and the tacho bars, fill the parent with
   * just the map — used inside the driver workspace's tabbed widget panel. */
  bare?: boolean
  /** With `bare`, a static preview: map + route only — no map controls,
   * panels or CTAs. The Agent thread's route-summary widget. */
  snapshot?: boolean
  hideRoutePanel?: boolean
}

const ChatMap: React.FC<ChatMapProps> = ({
  chat,
  driverJid,
  chatContainerRef,
  onHide,
  bare = false,
  snapshot = false,
  hideRoutePanel = false,
}) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const mobileMapVisible = useAppSelector(isMobileMapVisible)
  const locationJid = useAppSelector(selectLocationJidFromChatJid(chat.jid))

  const isReadOnlyMap = useIsReadOnlyMap(chat.jid)
  const isSmallScreen = useMediaQuery("(max-width: 900px)")

  const scrollableDivSize = useContainerResize(chatContainerRef, isMobile)

  const toggleMobileMap = useCallback(() => {
    if (mobileMapVisible) {
      dispatch(hideMobileMap())
      setMobileRoute(mobileRoutes.CHAT)
    } else {
      dispatch(showMobileMap())
      setMobileRoute(mobileRoutes.CHAT_MAP)
    }
  }, [dispatch, mobileMapVisible])

  const { tachos, isDoubleCrew } = useChatTachos(chat.jid)

  const { myJid } = useContext(XmppContext)
  let vhost = ""
  try {
    vhost = extractVhostFromUser(myJid?.toString())
  } catch (e) {
    console.error(e)
  }
  const { configured: naviConfigured } = useNaviConfig(vhost)
  const plannedOrdersSelector = useMemo(
    () => findPlannedOrdersForVehicle(chat.jid),
    [chat.jid],
  )
  const plannedOrdersCount = useAppSelector(plannedOrdersSelector).length
  // Whether this vehicle has planned orders is decided purely from the store,
  // which nothing here keeps fresh — the planner's own fetch on mount is the
  // only refresh in the app. Pull (throttled) when a vehicle chat opens, so
  // the tab and its count reflect the server rather than a stale snapshot of
  // an order since reassigned, unassigned, or dropped from the pool.
  useEffect(() => {
    if (!naviConfigured) return
    void dispatch(fetchPlannedOrdersIfStale())
  }, [dispatch, naviConfigured, chat.jid])
  const [activeMapTab, setActiveMapTab] = useState<"map" | "planned">("map")
  const showPlannedOrdersTabs = naviConfigured && plannedOrdersCount > 0
  const isPlannedTabActive = showPlannedOrdersTabs && activeMapTab === "planned"

  const tachoEnabled = true

  const tachoBars = tachos.length > 0 && tachoEnabled && (
    <div className="flex w-full flex-col bg-sidebar">
      {tachos.map(({ tacho }) => (
        <TimeProgressBar
          key={tacho.jid}
          usedSecondsToday={tacho.usedSecondsToday}
          remainingSecondsToday={tacho.remainingSecondsToday}
          remainingSecondsThisWeek={tacho.remainingSecondsThisWeek}
          remainingWeeklyRestSeconds={tacho.remainingWeeklyRestSeconds}
          remainingSecondsTwoWeeks={tacho.remainingSecondsTwoWeeks}
          remainingSecondsOfUninterruptedDriving={
            tacho.remainingSecondsOfUninterruptedDriving
          }
          dayEndTimestamp={tacho.dayEndTimestamp}
          remainingDailyExtensions={tacho.remainingDailyExtensions}
          isDoubleCrew={isDoubleCrew}
          driverName={tacho.driverName}
          legalCounters={tacho.legalCounters}
          crew={tacho.crew}
          timestamp={tacho.timestamp}
        />
      ))}
    </div>
  )

  if (bare && !isMobile && !isSmallScreen) {
    return (
      <div
        className={cn(
          "flex h-full min-h-0 flex-col overflow-hidden",
          // Static preview: drop the leftover maplibre controls + attribution.
          snapshot && "[&_.maplibregl-control-container]:!hidden",
        )}
      >
        <div className="relative flex min-h-0 flex-1 flex-col">
          <RouteView
            driverJid={driverJid}
            locationJid={locationJid}
            containerSize={scrollableDivSize}
            isReadOnlyMap={isReadOnlyMap}
            isSnapshot={snapshot}
            hideRoutePanel={hideRoutePanel}
          />
        </div>
      </div>
    )
  }

  return isMobile || isSmallScreen ? (
    <Drawer
      isOpen={mobileMapVisible}
      onClose={toggleMobileMap}
      position="top"
      size={window.innerHeight}
      canOutsideClickClose={true}
      canEscapeKeyClose={true}
      className={css.mobileMapDrawer}
      style={{ height: `${window.innerHeight}px` }}
    >
      <div
        className={css.mobileMapContainer}
        style={{ flex: 1, width: "100%" }}
      >
        {mobileMapVisible && (
          <div style={{ height: `${window.innerHeight}px`, width: "100%" }}>
            <RouteView
              key={driverJid}
              driverJid={driverJid}
              locationJid={locationJid}
              isReadOnlyMap={false}
              containerSize={{
                width: window.innerWidth,
                height: window.innerHeight,
              }}
            />
          </div>
        )}
      </div>
    </Drawer>
  ) : (
    <div className="flex h-full w-full flex-col">
      {!isMobile && !isSmallScreen && tachoBars}
      {showPlannedOrdersTabs && (
        <div className="flex items-center gap-1 border-b bg-sidebar px-2 py-1">
          <button
            type="button"
            className={`rounded-md px-2.5 py-1 text-xs font-medium ${
              !isPlannedTabActive
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
            onClick={() => setActiveMapTab("map")}
          >
            {t("maps.chatMap.mapTab")}
          </button>
          <button
            type="button"
            className={`rounded-md px-2.5 py-1 text-xs font-medium ${
              isPlannedTabActive
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
            onClick={() => setActiveMapTab("planned")}
          >
            {t("maps.chatMap.plannedOrdersTab", { number: plannedOrdersCount })}
          </button>
        </div>
      )}
      {isPlannedTabActive && (
        <div className="min-h-0 flex-1">
          <VehiclePlannedOrders chatJid={chat.jid} />
        </div>
      )}
      {/* Wrap the map in its own positioning context so the hide button overlays
          only the map area and never covers the tacho panel above it. The button
          mirrors the fullscreen control (right:10 top:105) and stacks directly
          below it, so it matches that control's size and stays clear of the
          native zoom/compass control in the top-right corner. */}
      <div
        className={`relative min-h-0 flex-1 flex-col ${
          isPlannedTabActive ? "hidden" : "flex"
        }`}
      >
        {onHide && (
          <div
            style={{
              position: "absolute",
              top: "140px",
              right: "10px",
              zIndex: 10,
            }}
          >
            <BpButton
              onClick={onHide}
              icon="drawer-right"
              className="dark:bg-gray-700 dark:text-white"
              aria-label={t("maps.chatMap.hideMap")}
              title={t("maps.chatMap.hideMap")}
              id="desktopHideMapButton"
            />
          </div>
        )}
        <RouteView
          driverJid={driverJid}
          locationJid={locationJid}
          containerSize={scrollableDivSize}
          isReadOnlyMap={isReadOnlyMap}
        />
      </div>
    </div>
  )
}

export default ChatMap
